import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Container,
  Section,
  H3Text1,
  NormalSpan,
} from 'pages/charterQuote/components/contents/Content.style';
import Header from 'pages/charterQuote/components/contents/Header';
import { Link } from 'react-router-dom';

const TypeContainer = styled.article`
  display: flex;
  justify-content: space-between;
  align-items: center;
  & > h3 {
    font-weight: 700;
  }
  img {
    max-width: 500px;
  }
`;

const TypeList = styled.ul`
  & > li {
    margin: 12px 0;
  }
`;

const Benefits = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(175px, 1fr));
  align-items: stretch;
  gap: 16px;
  grid-auto-rows: 1fr;
`;

const Benefit = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #f4f6fc;
  padding: 16px;
  min-height: 357px;
  height: 100%;

  img {
    width: 100px;
  }
`;

const CategoryContainer = styled.div`
  max-width: 524px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
  border-radius: 4px;
  background: ${(props) => (props.$isActive ? '#0063db' : '#f4f6fc')};
  transform: ${(props) => (props.$isActive ? 'translateY(-4px)' : 'translateY(0)')};
  color: ${(props) => (props.$isActive ? '#fff' : 'var(--normal-h2-h3-color)')};
  transition: background-color 300ms ease, color 400ms ease, transform 200ms ease;
`;

const BenefitDesc = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BenefitSection = styled(Section)`
  gap: 50px;
`;

const CategoryLink = styled(Link)`
  font-size: 20px !important;
  font-weight: 600;
  color: var(--normal-h2-h3-color);
  line-height: normal;
`;

const categories = [
  {
    type: 'Light Jets',
    url: 'Light Jets',
    alt: 'light private jet icon – short range private jet for regional flight',
    description: `Ideal for short trips and small groups (up to six passengers), light jets
    are perfect for regional flights. They offer a quick, cost-effective way to reach your
    destination without compromising on comfort.`,
    benefit: `From entering your trip details to selecting the ideal aircraft, booking your private jet has never been easier.`,
  },
  {
    type: 'Midsize Jets',
    url: 'Midsize Jets',
    alt: 'midsize private jet icon – versatile private jet for business travel',
    description: `A great option for small to medium-sized groups (up to eight passengers), super midsize jets provide additional space, more amenities, and longer range than lighter jets, making them ideal for both business and leisure trips.`,
    benefit: `These jets strike the perfect balance between cost and comfort. With a bit more space and range, they’re ideal for small to medium-sized groups, offering amenities like larger cabins and better onboard facilities.`,
  },
  {
    type: 'Large Jets',
    url: 'Super-Midsize Jets',
    alt: 'large private jet icon – long-distance jet charter option',
    description: `Best suited for longer flights and larger groups (up to 12 passengers), larger jets offer luxurious interiors with spacious cabins, advanced amenities, and the ability to cover extended distances and round trips in comfort and style.`,
    benefit: `Offering ample room, large jets provide enhanced luxury and comfort. With longer range and larger cabins, they’re ideal for long-haul travel, ensuring you and your guests enjoy the highest level of comfort during your flight.`,
  },
  {
    type: 'Heavy Jets',
    url: 'Heavy Jets',
    alt: 'heavy jet charter icon – large cabin private jet type',
    description: `The ultimate in luxury and capability, heavy jets cater to larger groups and long-distance flights. With the ability to carry more passengers and cargo, they’re the perfect choice for high-end travel experiences when you’re flying private.`,
    benefit: `The ultimate in luxury and capability, heavy jets cater to larger groups and long-distance flights. With the ability to carry more passengers and cargo, they’re the perfect choice for high-end travel experiences when you’re flying private.`,
  },
];

const Category = ({ category, isActive }) => {
  return (
    <CategoryContainer $isActive={isActive}>
      <H3Text1 $isActive={isActive}>{category.type}</H3Text1>
      <NormalSpan $isActive={isActive}>{category.description}</NormalSpan>
    </CategoryContainer>
  );
};

const FleetType = () => {
  const [activeType, setActiveType] = useState(categories[0]);
  return (
    <Container>
      <Section>
        <Header
          title="Types of Aircraft Available"
          desc={[
            "There are different charter aircraft to suit different travel needs, whether you'replanning a short flight or a long-haul journey.",
            'Below are the main types of private jets available, with each aircraft size designed to provide the best experience.',
          ]}
        />
        <TypeContainer>
          <TypeList>
            {categories.map((category) => {
              return (
                <li key={category.type} onClick={() => setActiveType(category)}>
                  <Category category={category} isActive={category.type === activeType.type} />
                </li>
              );
            })}
          </TypeList>

          <img src={`imgs/svg/charterQuote/${activeType.type}.svg`} alt={activeType.alt} />
        </TypeContainer>
      </Section>

      <BenefitSection>
        <Header
          title={
            <>
              Benefits of Different
              <br />
              Aircraft Types
            </>
          }
          desc={[
            'Each type of aircraft in our fleet offers distinct advantages. From quick, budget-friendly trips to luxurious long-haul experiences, understanding the benefits of each aircraft type can help you make the right decision.',
          ]}
        />
        <Benefits>
          {categories.map((category) => {
            return (
              <Benefit key={category.type}>
                <img src={`imgs/svg/charterQuote/${category.type}.svg`} alt={category.alt} />

                <BenefitDesc>
                  <CategoryLink to={`/private-jets#${encodeURIComponent(category.url)}`}>
                    <H3Text1>{category.type}</H3Text1>
                  </CategoryLink>
                  <NormalSpan>{category.benefit}</NormalSpan>
                </BenefitDesc>
              </Benefit>
            );
          })}
        </Benefits>
      </BenefitSection>
    </Container>
  );
};

export default FleetType;
